import { exitDemoMode, setActiveFarmId } from "@/base/async-storage"
import { ENVIRONMENT, isTargetDatabase } from "@/base/environment"
import { prepareDemoEnvironmentAsync } from "@/base/farmhq-api"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import * as Models from "@/redux/models"
import { useRootSelector } from "@/redux/useRootSelector"
import { getIsChangingActiveFarmFromState } from "@/redux/user-farms.reducer"
import { isDemoFarmId } from "@/redux/user-farms.selectors"
import {
  AlertBodyText,
  AlertCard,
  AppIcons,
  Badge,
  Box,
  Button,
  Divider,
  FlatList,
  ListItemTextPrimary,
  Pressable,
  Row,
  ScrollView,
  Theme,
  useThemedValue,
} from "@/theme"
import { createSelector } from "@reduxjs/toolkit"
import { useRouter } from "expo-router"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  getActiveFarmFromState,
  getDemoFarmIdOrActiveFarmIdFromState,
  getIsDemoModeActiveFromState,
} from "@/redux/selectors"

function keyExtractor(item: { id: number }): string {
  return `${item.id}`
}

/**
 * Get all farm accounts for the user that are not the current active farm
 * and are not demo farms
 */
const getNonActiveFarmsFromState = createSelector(
  Models.userFarm.selectAll,
  getDemoFarmIdOrActiveFarmIdFromState,
  (farms, activeFarmId) =>
    farms.filter((farm) => {
      if (isDemoFarmId(farm.id)) {
        return false
      }
      if (activeFarmId === farm.id) {
        return false
      }
      return true
    }),
)

function RenderFarmListItem({
  endElement,
  name,
}: {
  endElement?: React.JSX.Element
  name: string
}) {
  if (typeof endElement === "undefined") {
    endElement = <AppIcons.ListItemEnd />
  }
  return (
    <Row justifyContent={"space-between"} py={"$2"}>
      {/* <ListNumber value={id} /> */}
      <ListItemTextPrimary>{name}</ListItemTextPrimary>
      {endElement}
    </Row>
  )
}

/**
 *
 */
function ActiveFarm(): React.JSX.Element | null {
  const { t } = useTranslation("account")
  const activeFarm = useRootSelector(getActiveFarmFromState)
  const isDemoActive = useRootSelector(getIsDemoModeActiveFromState)
  if (isDemoActive || typeof activeFarm === "undefined") {
    return null
  }
  return (
    <React.Fragment>
      <RenderFarmListItem
        name={activeFarm.name}
        endElement={
          <Badge colorScheme={"$success"}>
            {t("activeFarmTitle", { ns: "account" })}
          </Badge>
        }
      />
      <Box my={"$0"}>
        <Divider />
      </Box>
    </React.Fragment>
  )
}

function PrepareDemoButton({
  onChangeFarmsSuccess,
}: {
  onChangeFarmsSuccess: () => void
}) {
  const { t } = useTranslation("demoMode")
  const { dispatch, handleError, isLoading } = useRequestHelpers(
    "PrepareDemoEnvironment",
  )

  let text = t("loadDemoButton")
  if (!isTargetDatabase("PROD")) {
    text += ` (${ENVIRONMENT.targetDatabaseName})`
  }

  return (
    <Button
      IconComponent={"Truck"}
      isLoading={isLoading}
      text={text}
      onPress={() => {
        dispatch(prepareDemoEnvironmentAsync())
          .unwrap()
          .then(onChangeFarmsSuccess)
          .catch((error) => {
            handleError(error, { toastMessage: "default" })
          })
      }}
    />
  )
}
export function ChangeFarmsList() {
  const { t } = useTranslation("account")
  const { dispatch, handleError } = useRequestHelpers()
  const data = useRootSelector(getNonActiveFarmsFromState)
  const router = useRouter()
  const onChangeFarmsSuccess = () => {
    router.navigate("/")
  }
  const isChangingFarms = useRootSelector(getIsChangingActiveFarmFromState)
  const isDemoModeActive = useRootSelector(getIsDemoModeActiveFromState)
  const backgroundColor = useThemedValue({
    dark: Theme.COLORS.$background.dark,
    light: Theme.COLORS.$paper.light,
  })
  if (isDemoModeActive) {
    return (
      <ScrollView>
        <AlertCard titleText={t("isActiveText", { ns: "demoMode" })}>
          <Box key={"demo"} flex={1}>
            <AlertBodyText key={"body"}>
              {t("warnings.someFeaturesDisabled", { ns: "demoMode" })}
            </AlertBodyText>
          </Box>
          <Box ml={"auto"} mt={"$4"}>
            <Button
              key={"exit"}
              IconComponent={"SignOut"}
              text={t("exitDemoMode")}
              onPress={() => {
                dispatch(exitDemoMode())
                  .unwrap()
                  .catch((error) => {
                    handleError(error, { toastMessage: "default" })
                  })
              }}
            />
          </Box>
        </AlertCard>
      </ScrollView>
    )
  }
  return (
    <FlatList
      ItemSeparatorComponent={Divider}
      ListHeaderComponentStyle={{ backgroundColor }}
      data={data}
      id={"changeFarmAccount"}
      keyExtractor={keyExtractor}
      stickyHeaderIndices={[0]}
      ListFooterComponent={
        <Box p={"$4"}>
          <PrepareDemoButton onChangeFarmsSuccess={onChangeFarmsSuccess} />
        </Box>
      }
      ListHeaderComponent={
        <Box px={"$4"}>
          <ActiveFarm />
        </Box>
      }
      renderItem={({ item }) => {
        const isDisabled = isChangingFarms
        return (
          <Pressable
            key={item.id}
            disabled={isDisabled}
            px={"$4"}
            onPress={() => {
              if (!isDisabled) {
                dispatch(setActiveFarmId(item.id))
                  .then(onChangeFarmsSuccess)
                  .catch((error) =>
                    handleError(error, { toastMessage: "default" }),
                  )
              }
            }}
          >
            <Box py={"$4"}>
              <RenderFarmListItem name={item.name} />
            </Box>
          </Pressable>
        )
      }}
    />
  )
}
